import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import { mergeResultsRRF } from "./rrf";
import type { ISearchState, SearchErrorKind, SearchStreamEvent } from "./search.types";
import { SEARCH_API_VERSION } from "./searchApiVersion";
import { isTenantNotReady } from "./tenantNotReady";
import { useAuthToken } from "./useAuthToken";

export interface IUseSearchOptions {
    /**
     * Ends a search as a `timeout` error when it has not committed a ranking
     * this long after it started. Off when omitted.
     */
    commitTimeoutMs?: number;
    /**
     * The records the user opened recently, newest first, as `entity:id`.
     * Read at the moment each search is sent, so a record opened a second ago
     * counts. The service ranks them a little higher.
     */
    getRecentRefs?: () => string[];
    /**
     * The one job the user is viewing. The service ranks records on it a
     * little higher, without filtering out other jobs' records.
     */
    contextJobsiteId?: string;
}

/**
 * Hook for SSE-based AI search against the BTNet-Search service.
 *
 * Streams results progressively as they arrive and merges them with RRF.
 *
 * Usage:
 *   const { doSearch, state, cancel } = useSearch();
 *   doSearch("foundation repair contractors");
 */
export function useSearch(options?: IUseSearchOptions) {
    const commitTimeoutMs = options?.commitTimeoutMs;
    const getRecentRefs = options?.getRecentRefs;
    // A ref, so a caller passing a fresh closure each render doesn't rebuild
    // the fetch callbacks. Synced in an effect, so a render React discards
    // can't leave its closure behind.
    const getRecentRefsRef = useRef(getRecentRefs);
    useEffect(() => {
        getRecentRefsRef.current = getRecentRefs;
    }, [getRecentRefs]);
    // A ref for the same reason, so a job change doesn't rebuild them either.
    const contextJobsiteId = options?.contextJobsiteId;
    const contextJobsiteIdRef = useRef(contextJobsiteId);
    useEffect(() => {
        contextJobsiteIdRef.current = contextJobsiteId;
    }, [contextJobsiteId]);
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const searchConfig = appDefaultInfo?.searchServiceConfiguration;
    const searchBaseUrl = searchConfig?.apiBaseUrl ?? "";
    const searchAudience = searchConfig?.authAudience ?? "";

    const { getToken, clearToken } = useAuthToken(searchAudience);
    const abortControllerRef = useRef<AbortController | null>(null);
    // Monotonic sequence number bumped on each search/cancel. Used only to
    // discard stale SSE events from a superseded request — unrelated to the
    // telemetry correlation uuid (state.searchId / searchUuidRef).
    const searchSequenceIdRef = useRef(0);
    const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [state, setState] = useState<ISearchState>({
        searchId: "",
        traceId: null,
        searchQuery: "",
        results: [],
        committedResults: null,
        previousCommitted: null,
        searchType: null,
        entityScope: "broad",
        scopedEntities: new Set(),
        phase: "idle",
        error: null,
        errorKind: null,
        searchStartTime: null,
    });

    /** Ends the search as a whole, so the view can offer a retry. */
    const fail = useCallback((errorKind: SearchErrorKind, error: string) => {
        setState((prev) => ({ ...prev, phase: "error", error, errorKind }));
    }, []);

    const clearCommitTimer = useCallback(() => {
        if (commitTimerRef.current !== null) {
            clearTimeout(commitTimerRef.current);
            commitTimerRef.current = null;
        }
    }, []);

    const handleEvent = useCallback(
        (event: SearchStreamEvent, searchSequenceId: number) => {
            if (searchSequenceId !== searchSequenceIdRef.current) return;

            switch (event.type) {
                case "intent": {
                    const scope = event.entity_scope ?? "broad";
                    const scoped = new Set<string>(event.scoped_entities ?? []);

                    setState((prev) => ({
                        ...prev,
                        entityScope: scope,
                        scopedEntities: scoped,
                    }));
                    break;
                }

                case "search_type": {
                    const decision = event.decision;
                    if (decision === undefined) break;
                    setState((prev) => ({
                        ...prev,
                        searchType: { query: prev.searchQuery, decision },
                    }));
                    break;
                }

                case "result": {
                    const resultData = event.results;
                    if (!resultData) break;

                    const stage = event.stage ?? "default";
                    const op = { ...resultData.op, stage };
                    const result = { ...resultData, op };

                    setState((prev) => ({
                        ...prev,
                        phase: prev.phase === "loading" ? "streaming" : prev.phase,
                        results: mergeResultsRRF(
                            prev.results,
                            result,
                            prev.entityScope,
                            prev.scopedEntities,
                        ),
                    }));
                    break;
                }

                case "trace": {
                    setState((prev) => ({ ...prev, traceId: event.trace_id ?? null }));
                    break;
                }

                case "committed": {
                    clearCommitTimer();
                    // Replaced, not merged: the service's ranking is authoritative.
                    setState((prev) => ({
                        ...prev,
                        committedResults: Array.isArray(event.ranked) ? event.ranked : [],
                    }));
                    break;
                }

                // One stage failed; the service carries on and still sends `done`,
                // so the search as a whole has not failed.
                case "error": {
                    setState((prev) => ({ ...prev, error: event.error ?? "Search error" }));
                    break;
                }

                case "done": {
                    setState((prev) => ({
                        ...prev,
                        // Either list counts: `results` is the streamed fusion,
                        // `committedResults` the service's own ranking.
                        phase:
                            prev.results.length > 0 || (prev.committedResults?.length ?? 0) > 0
                                ? "done"
                                : "empty",
                    }));
                    break;
                }
            }
        },
        [clearCommitTimer],
    );

    const fetchSSE = useCallback(
        async (query: string, searchId: string, signal: AbortSignal, searchSequenceId: number) => {
            const isCurrent = () => searchSequenceId === searchSequenceIdRef.current;

            /** Null when there is nothing to send: no token, or superseded. */
            const send = async (): Promise<Response | null> => {
                let token: string;
                try {
                    token = await getToken();
                } catch {
                    if (isCurrent()) {
                        fail("auth", "Failed to authenticate with search service");
                    }
                    return null;
                }
                // Superseded or closed while the token was minting, so the
                // request is no longer worth sending.
                if (!isCurrent()) return null;
                const recentRefs = getRecentRefsRef.current?.() ?? [];
                const jobsiteId = contextJobsiteIdRef.current;
                return fetch(`${searchBaseUrl}${SEARCH_API_VERSION}/search`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        query,
                        search_id: searchId,
                        ...(recentRefs.length > 0 ? { recent_refs: recentRefs } : {}),
                        ...(jobsiteId ? { context_jobsite_id: jobsiteId } : {}),
                    }),
                    signal,
                });
            };

            try {
                let response = await send();
                if (response === null || !isCurrent()) return;

                // Usually a cached token that expired early or was revoked, so
                // a fresh one gets one more try. Only one, so a real rejection
                // still ends the search.
                if (response.status === 401) {
                    clearToken();
                    response = await send();
                    if (response === null || !isCurrent()) return;
                    if (response.status === 401) {
                        clearToken();
                        fail("auth", "Authentication expired. Please try again.");
                        return;
                    }
                }

                const tenantNotReady = await isTenantNotReady(response);
                // Superseded or closed while the body was being read.
                if (!isCurrent()) return;
                if (tenantNotReady) {
                    fail("unavailable", "Search is not available for this builder");
                    return;
                }

                if (!response.ok || !response.body) {
                    fail("server", `Search failed (${response.status})`);
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                let currentEvent = "";
                let sawDone = false;

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop()!;

                    for (const line of lines) {
                        if (line.startsWith("event: ")) {
                            currentEvent = line.slice(7).trim();
                        } else if (line.startsWith("data: ")) {
                            try {
                                const eventData = JSON.parse(line.slice(6)) as Record<
                                    string,
                                    unknown
                                >;
                                handleEvent(
                                    {
                                        type: currentEvent,
                                        ...eventData,
                                    } as SearchStreamEvent,
                                    searchSequenceId,
                                );
                                sawDone ||= currentEvent === "done";
                            } catch {
                                // Skip malformed JSON
                            }
                            currentEvent = "";
                        }
                    }
                }

                // The service always ends with `done`, so a body that closes
                // without it was cut off, typically by the instance going away.
                if (!sawDone && isCurrent()) {
                    fail("network", "Connection to search service failed");
                }
            } catch (err: unknown) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                if (!isCurrent()) return;
                fail("network", "Connection to search service failed");
            }
        },
        [searchBaseUrl, getToken, clearToken, handleEvent, fail],
    );

    const doSearch = useCallback(
        (query: string) => {
            if (!searchBaseUrl) {
                setState((prev) => ({
                    ...prev,
                    searchQuery: query,
                    phase: "error",
                    error: "Search service URL not configured",
                    errorKind: "config",
                }));
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            clearCommitTimer();

            const controller = new AbortController();
            abortControllerRef.current = controller;

            searchSequenceIdRef.current += 1;
            const searchSequenceId = searchSequenceIdRef.current;

            const searchUuid = uuidv4();

            setState((prev) => ({
                searchId: searchUuid,
                // Cleared with the search it belonged to: a stale trace id
                // would point telemetry at the previous query's spans.
                traceId: null,
                searchQuery: query,
                results: [],
                committedResults: null,
                // Carried past a search that never committed, so fast typing
                // still has the last real list to show.
                previousCommitted:
                    prev.committedResults !== null
                        ? { query: prev.searchQuery, hits: prev.committedResults }
                        : prev.previousCommitted,
                // Kept until this search sends its own: whether the previous
                // query's verdict still applies is the caller's call.
                searchType: prev.searchType,
                entityScope: "broad",
                scopedEntities: new Set(),
                phase: "loading",
                error: null,
                errorKind: null,
                searchStartTime: Date.now(),
            }));

            if (commitTimeoutMs !== undefined) {
                commitTimerRef.current = setTimeout(() => {
                    commitTimerRef.current = null;
                    if (searchSequenceId !== searchSequenceIdRef.current) return;
                    // Retired first, so a stream that wakes up late cannot
                    // land on top of the error.
                    searchSequenceIdRef.current += 1;
                    controller.abort();
                    fail("timeout", "Search timed out");
                }, commitTimeoutMs);
            }

            void fetchSSE(query, searchUuid, controller.signal, searchSequenceId).finally(() => {
                if (searchSequenceId === searchSequenceIdRef.current) {
                    clearCommitTimer();
                }
            });
        },
        [searchBaseUrl, fetchSSE, commitTimeoutMs, clearCommitTimer, fail],
    );

    const cancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        clearCommitTimer();
        // Invalidate any late SSE events still flushing through handleEvent
        // so they can't setState({phase: "streaming"}) after we've gone idle.
        searchSequenceIdRef.current += 1;
        setState((prev) => ({
            ...prev,
            // Whatever is typed next is a new query, which this search's
            // rankings and failure say nothing about.
            committedResults: null,
            previousCommitted: null,
            searchType: null,
            error: null,
            errorKind: null,
            phase: "idle",
        }));
    }, [clearCommitTimer]);

    /**
     * Abandons the in-flight search on unmount. Refs only: setting state here
     * would land on an unmounted component.
     */
    useEffect(
        () => () => {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
            searchSequenceIdRef.current += 1;
            if (commitTimerRef.current !== null) {
                clearTimeout(commitTimerRef.current);
            }
        },
        [],
    );

    return { doSearch, cancel, state };
}
